<script setup>
import { computed } from 'vue'
import { Link, router, usePage } from '@inertiajs/vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiCard from '@/Components/UI/UiCard.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import CustomerChannels from '@/Components/CustomerChannels.vue'
import { useI18n } from 'vue-i18n'

defineOptions({ layout: AppLayout })

const props = defineProps({
  host:           { type: Object, required: true },
  properties:     { type: Array,  default: () => [] },
  channels:       { type: Array,  default: () => [] },
  conversations:  { type: Array,  default: () => [] },
  portal_account: { type: Object, default: null }
})

const { t } = useI18n()
const isAdmin = usePage().props.auth?.user?.role === 'admin'

const hasPersonDetails = computed(() =>
  Boolean(props.host.first_name || props.host.last_name)
)

function fmtRelative(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function archive() {
  if (!confirm(t('property_hosts.show.archive_confirm', { name: props.host.name }))) return
  router.delete(`/property_hosts/${props.host.id}`)
}

function approvalStyle(state) {
  if (state === 'approved') return { background: 'color-mix(in oklch, var(--ok) 14%, var(--surface))', color: 'var(--ok)' }
  if (state === 'pending')  return { background: 'color-mix(in oklch, var(--warn) 14%, var(--surface))', color: 'var(--warn)' }
  return { background: 'color-mix(in oklch, var(--danger) 14%, var(--surface))', color: 'var(--danger)' }
}
</script>

<template>
  <PageShell
    :title="host.name"
    back-href="/property_hosts"
    :back-label="t('property_hosts.show.back')"
  >
    <template #actions>
      <Link v-if="isAdmin" :href="`/property_hosts/${host.id}/edit`">
        <UiButton variant="ghost" size="sm">{{ t('property_hosts.show.edit') }}</UiButton>
      </Link>
      <UiButton v-if="isAdmin" variant="danger" size="sm" @click="archive">
        {{ t('property_hosts.show.archive') }}
      </UiButton>
    </template>

    <!-- Contact / summary card -->
    <UiCard>
      <div class="flex flex-wrap items-start gap-4">
        <div class="flex-1 min-w-0">
          <div v-if="hasPersonDetails" class="fw-value text-[color:var(--ink)] mb-1">
            {{ host.full_name }}
          </div>
          <div class="text-sm text-[color:var(--inkSoft)] space-y-1">
            <div v-if="host.contact_phone">📞 {{ host.contact_phone }}</div>
            <div v-if="host.contact_email">✉ {{ host.contact_email }}</div>
          </div>
        </div>
        <div class="text-right shrink-0">
          <div class="text-xs text-[color:var(--muted)]">{{ t('property_hosts.index.columns.properties') }}</div>
          <div class="fw-value tabular-nums text-2xl text-[color:var(--ink)]">{{ host.properties_count }}</div>
          <div
            v-if="host.pending_count > 0"
            class="text-xs mt-0.5"
            style="color: var(--warn)"
          >
            {{ host.pending_count }} pending
          </div>
        </div>
      </div>
      <p v-if="host.notes" class="mt-3 text-sm text-[color:var(--inkSoft)] whitespace-pre-line">
        {{ host.notes }}
      </p>
    </UiCard>

    <!-- Properties -->
    <UiCard>
      <h3 class="text-sm font-semibold mb-3">{{ t('property_hosts.show.sections.properties') }}</h3>
      <div
        v-if="properties.length === 0"
        class="text-sm text-[color:var(--muted)] py-6 text-center"
      >
        {{ t('property_hosts.show.no_properties') }}
      </div>
      <table v-else class="w-full text-sm">
        <thead class="text-xs uppercase tracking-wider text-[color:var(--muted)]">
          <tr>
            <th class="text-left py-2 w-12" />
            <th class="text-left py-2">Title</th>
            <th class="text-left py-2 hidden md:table-cell">Area</th>
            <th class="text-left py-2">Status</th>
            <th class="text-right py-2">Price</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-[color:var(--line)]">
          <tr v-for="property in properties" :key="property.id" class="hover:bg-[color:var(--surfaceAlt)]">
            <td class="py-2 pr-2">
              <img
                v-if="property.photo_url"
                :src="property.photo_url"
                class="w-10 h-10 rounded object-cover"
                alt=""
              />
              <div
                v-else
                class="w-10 h-10 rounded bg-[color:var(--surfaceAlt)] flex items-center justify-center text-[color:var(--muted)] text-lg"
              >
                🏠
              </div>
            </td>
            <td class="py-2 pr-3">
              <Link
                v-if="property.approval_state === 'pending'"
                href="/admin/property_approvals"
                class="text-[color:var(--accent)] hover:underline font-medium"
                @click.stop
              >
                {{ property.title }}
              </Link>
              <span v-else class="font-medium text-[color:var(--ink)]">{{ property.title }}</span>
            </td>
            <td class="py-2 pr-3 text-[color:var(--inkSoft)] hidden md:table-cell">
              {{ property.area || '—' }}
            </td>
            <td class="py-2 pr-3">
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium"
                :style="approvalStyle(property.approval_state)"
              >
                {{ property.approval_state }}
              </span>
            </td>
            <td class="py-2 text-right fw-value tabular-nums text-[color:var(--ink)]">
              {{ property.price_per_night || '—' }}
            </td>
          </tr>
        </tbody>
      </table>
    </UiCard>

    <!-- Channels -->
    <UiCard v-if="channels.length">
      <h3 class="text-sm font-semibold mb-3">{{ t('property_hosts.show.sections.contact') }}</h3>
      <CustomerChannels :model-value="channels" :readonly="true" />
    </UiCard>

    <!-- Conversations -->
    <UiCard>
      <div class="flex items-center justify-between mb-3">
        <h3 class="text-sm font-semibold">{{ t('property_hosts.show.sections.conversations') }}</h3>
        <Link href="/inbox" class="text-xs text-[color:var(--accent)] underline">
          {{ t('common.openInbox') || 'Open inbox' }}
        </Link>
      </div>
      <div
        v-if="conversations.length === 0"
        class="text-sm text-[color:var(--muted)] py-6 text-center"
      >
        {{ t('property_hosts.show.no_conversations') }}
      </div>
      <ul v-else class="divide-y divide-[color:var(--line)]">
        <li v-for="c in conversations" :key="c.id">
          <Link
            :href="`/inbox/conversations/${c.id}`"
            class="flex items-center gap-3 py-2 hover:bg-[color:var(--surfaceAlt)] -mx-2 px-2 rounded"
          >
            <span class="text-xs uppercase tracking-wider text-[color:var(--muted)] w-24 truncate">
              {{ (c.channel_type || '').replace(/^Channel::/, '') }}
            </span>
            <span class="flex-1 text-sm text-[color:var(--ink)] truncate">
              {{ c.contact_name || '—' }}
            </span>
            <span
              v-if="c.unread > 0"
              class="text-[10px] px-1.5 py-0.5 rounded-full"
              :style="{ background: 'var(--accentSoft)', color: 'var(--accent)' }"
            >
              {{ c.unread }}
            </span>
            <span class="text-xs text-[color:var(--muted)]">
              {{ fmtRelative(c.last_message_at) }}
            </span>
          </Link>
        </li>
      </ul>
    </UiCard>

    <!-- Portal access -->
    <UiCard v-if="isAdmin">
      <h3 class="text-sm font-semibold mb-3">{{ t('property_hosts.show.sections.portal') }}</h3>
      <div v-if="portal_account" class="text-sm space-y-1">
        <div class="flex flex-wrap items-center gap-2">
          <span class="fw-value text-[color:var(--ink)]">{{ portal_account.email }}</span>
          <span
            class="px-2 py-0.5 rounded-full text-xs font-medium"
            :style="portal_account.status === 'active'
              ? { background: 'color-mix(in oklch, var(--ok) 14%, var(--surface))', color: 'var(--ok)' }
              : { background: 'color-mix(in oklch, var(--muted) 14%, var(--surface))', color: 'var(--muted)' }"
          >
            {{ t(`property_hosts.show.portal_status.${portal_account.status}`) }}
          </span>
        </div>
        <div v-if="portal_account.last_sign_in_at" class="text-xs text-[color:var(--muted)]">
          Last sign-in: {{ fmtRelative(portal_account.last_sign_in_at) }}
        </div>
        <div v-else class="text-xs text-[color:var(--muted)]">
          Never signed in
        </div>
        <div class="mt-2">
          <Link
            :href="`/admin/portal_accounts/${portal_account.id}`"
            class="text-sm text-[color:var(--accent)] hover:underline"
          >
            Manage portal account →
          </Link>
        </div>
      </div>
      <div v-else class="text-sm">
        <p class="mb-2 text-[color:var(--muted)]">{{ t('property_hosts.show.no_portal') }}</p>
        <Link
          :href="`/admin/portal_accounts/new?owner_type=PropertyHost&owner_id=${host.id}`"
          class="text-sm text-[color:var(--accent)] hover:underline"
        >
          {{ t('property_hosts.show.provision_portal') }}
        </Link>
      </div>
    </UiCard>
  </PageShell>
</template>
